/**
 * Pure rules for the task drawer's notes log (DD-050): when a note can be
 * saved, who wrote it, and when, in Italian.
 */

import { formatDate, formatDateTime, toValidDate } from "@/utils/date";
import type { VoiceClipView } from "@/core/voice";
import type { TaskNoteAuthor } from "../types";

/** A note whose author's person was deleted (TD-095). */
export const DELETED_AUTHOR_LABEL = "Persona eliminata";
export const UNNAMED_AUTHOR_LABEL = "Senza nome";

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** The server refuses an empty or blank body. */
export function canAddNote(body: string): boolean {
  return body.trim().length > 0;
}

export function noteAuthorLabel(author: TaskNoteAuthor | null): string {
  if (!author) return DELETED_AUTHOR_LABEL;
  return author.name?.trim() || UNNAMED_AUTHOR_LABEL;
}

/** "adesso", "5 min fa", "3 ore fa", "2 giorni fa", then the date ("1 ott").
 * `now` is a parameter so tests can pin it. */
export function noteRelativeTime(createdAt: string, now: number): string {
  const date = toValidDate(createdAt);
  if (!date) return "—";
  const elapsed = Math.max(0, now - date.getTime());
  if (elapsed < MINUTE) return "adesso";
  if (elapsed < HOUR) return `${Math.floor(elapsed / MINUTE)} min fa`;
  if (elapsed < DAY) {
    const hours = Math.floor(elapsed / HOUR);
    return hours === 1 ? "1 ora fa" : `${hours} ore fa`;
  }
  const days = Math.floor(elapsed / DAY);
  if (days < 7) return days === 1 ? "1 giorno fa" : `${days} giorni fa`;
  return formatDate(date, { locale: "it-IT", day: "numeric", month: "short" });
}

/** The full date and time, for the hover title. */
export function noteFullTime(createdAt: string): string {
  return formatDateTime(createdAt, { locale: "it-IT", dateStyle: "long", timeStyle: "short" });
}

/** A recording that exists nowhere else: still being recorded or waiting to
 * send, or a clip whose upload failed (its blob lives only in memory).
 * An upload in flight outlives the drawer. A rejected one cannot be resent. */
export function hasUnsentVoice(recorderUnsent: boolean, clips: VoiceClipView[]): boolean {
  return recorderUnsent || clips.some((clip) => clip.kind === "local" && clip.upload.status === "upload_failed");
}

/** What the drawer was doing when the unsent-recording prompt stopped it. */
export type PendingLeave = { kind: "close" } | { kind: "follow"; path: string };

/** Scarta closes the drawer, then resumes a followed link's navigation. */
export function pathAfterDiscard(pending: PendingLeave): string | null {
  return pending.kind === "follow" ? pending.path : null;
}
